import React, { useEffect } from "react";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import deutsche_logo from "./static/deutsche_logo.png"; // Deutsche Bank logo
import kjsce_logo from "./static/kjsce_logo.png"; // KJSCE logo
import c3_logo from "./static/c3_logo.png";

const Section = styled.section`
  padding: 4rem 2rem;
  // background: #ffffff;

  h2 {
    font-size: 2.5rem;
    text-align: center;
    margin-bottom: 2rem;
  }

  .timeline {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 800px;
    margin: 0 auto;

    .item {
      display: flex;
      align-items: center; /* Center logo with content */
      gap: 1rem;
      margin-bottom: 2rem;
      opacity: 0; /* Hidden initially for animation */

      .logo {
        flex-shrink: 0;
        width: 70px;
        height: 70px;
        align-self: center; /* Center logo vertically */
      }

      .content {
        .role {
          font-size: 1.2rem;
          font-weight: bold;
          color: #007aff;
        }

        .details {
          margin-top: 0.5rem;
          font-size: 1rem;
          color: #555;

          ul {
            list-style-type: disc;
            margin-left: 1.5rem;

            li {
              margin-bottom: 0.5rem; /* Space between each bullet */
            }
          }
        }

        .technologies {
          margin-top: 0.5rem;
          font-size: 0.9rem;
          color: #777;
          font-style: italic;
        }
      }
    }

    @media (max-width: 768px) {
      .item {
        flex-direction: column;
        align-items: center;

        .logo {
          margin-bottom: 1rem;
        }
      }
    }
  }
`;

const Experience = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2 });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const slideInVariant = {
    hidden: { opacity: 0, x: -100 },
    visible: (index) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, delay: index * 0.2 }, // Stagger animation
    }),
  };

  const experiences = [
    {
      logo: c3_logo,
      role: "Software Engineer | C3.ai",
      details: [
        "Designed and built a distributed AI-powered Root Cause Analysis (RCA) platform ingesting high-volume logs from Grafana, OpenSearch and client logs; reducing mean time to diagnosis (MTTD) by ~40% by correlating multi-source failure signals.",
        "Developed sandboxed execution infrastructure for production AI agents to run code in secured containerized environments",
        "Investigated and resolved concurrency bugs (deadlocks, memory leaks) in a multi-threaded Java service during PSR runs, improving system stability and unblocking weekly release cycles.",
        "Streamlined, automated and maintained builds using Jenkins, reducing build times by 60%.",
      ],
      technologies:
        "Java, Python, Django, React, Grafana, OpenAI API, OpenSearch, Jenkins, GitHub Actions, Distributed Tracing",
    },
    {
      logo: deutsche_logo,
      role: "Software Engineer | Deutsche Bank",
      details: [
        "Built a production RAG-based agentic system using Google Gemini that translates natural language to SQL, serving a 15TB hybrid data lake (Impala + BigQuery).",
        "Engineered an intelligent chat interface that reduced engineering bottlenecks by automating ad-hoc data requests, allowing the Annual Account Activity Review team to query 35+ countries of data via a simple chat interface.",
        "Engineered LLM responses in real-time database schemas, ensuring high-accuracy SQL generation and automated summarization.",
        "Automated classification pipeline for Institutional & Corporate clients by joining and deduplicating datasets across 4+ teams.",
        "Deployed and maintained applications on GKE (Kubernetes) with rolling updates via GitHub Actions CI/CD; containerized services support horizontal autoscaling across 3 environments (dev/staging/prod).",
        "Built a transaction-level observability dashboard using New Relic capturing latency/error metrics and improving debugging.",
      ],
      // details: [
      //   "Enabled real-time payments on an Instant Payments platform by developing distributed Java Spring Boot microservices processing 100+ TPS with an SLA of <8s/transaction, ensuring data consistency and fault tolerance across services.",
      //   "Developed a data synchronization pipeline replicating static data from Oracle SQL to NoSQL for low-latency lookups in real-time.",
      //   "Improved service resilience with health checks, retries, and fallbacks, reducing downtime and increasing reliability.",
      //   "Reduced codebase analysis time by ~40% by developing an internal AI-powered tool with role based access control (RBAC) and validating improvements through A/B testing, ensuring measurable productivity gains.",
      //   "Integrated Google Vertex AI for file summarization with optimized prompt caching, saving 16+ engineering hours/week.",
      //   "Deployed both systems using Docker + GKE with automated CI/CD pipelines (GitHub Actions) for consistent and scalable releases.",
      //   "Built a transaction-level observability dashboard using New Relic capturing latency/error metrics and improving debugging speed.",
      // ],
      technologies:
        "Python, FastAPI, React, New Relic, BigQuery, SQL, GitHub Actions, Java, GKE",
    },
    {
      logo: deutsche_logo,
      role: "Software Analyst Intern | Deutsche Bank",
      details: [
        "Developed a full-stack application for AI based legacy codebases analysis, enhancing productivity and reducing analysis time.",
        "Built a dynamic React frontend and Flask backend with secure role-based access control (RBAC) and enhanced user interaction.",
        "Integrated Google Vertex AI for file summarization with optimized caching, saving 16+ engineering hours/week.",
      ],
      technologies:
        "Python, Flask RESTful, React, GCP (Datastore, IAM, App Engine)",
      // details: [
      //   "Built an NLP-based analytics tool for ESG performance tracking, scraping structured data from bank and competitor newsrooms.",
      //   "Designed and deployed REST APIs using Flask on GCP App Engine with Datastore, achieving 99.9% uptime and high scalability.",
      //   "Created over 20 visual reports with interactive dashboards using Plotly Dash, enabling keyword-based analysis of connotation impact on scores across regions and streamlining insights into geographical variations from Google NLP API integration.",
      //   "Wrote unit tests with pytest to ensure 90%+ code coverage and support compliance.",
      // ],
      // technologies: "Python, Flask, GCP, Data Science",
    },
    {
      logo: kjsce_logo,
      role: "Software Engineering Intern | K.J. Somaiya College of Engineering",
      details: [
        "Engineered a water-logging prediction system by using Raspberry Pi 4 for real-time data acquisition, and Flutter framework for mobile application development. Utilized AWS for data transfer between sensors, AWS DynamoDB and the mobile application.",
        "Built a scalable data pipeline with AWS Lambda and DynamoDB for real-time ingestion of sensor data, reducing latency by 30ms.",
        "Tackled environmental challenges with innovative cloud solutions.",
      ],
      technologies:
        "Python, AWS (DynamoDB, Lambda functions, API Gateway), REST APIs, Github.",
    },
    {
      logo: kjsce_logo,
      role: "Software Engineering Intern | K.J. Somaiya College of Engineering",
      details: [
        "Collaborated on a plant disease detection system using CNNs and SVM.",
        "Achieved 91% accuracy in disease classification.",
        "Enhanced machine learning expertise in image processing and analysis.",
      ],
      technologies: "Python, TensorFlow, OpenCV, Google Colab",
    },
  ];

  return (
    <Section ref={ref}>
      <h2>Experience</h2>
      <div className="timeline">
        {experiences.map((experience, index) => (
          <motion.div
            className="item"
            key={index}
            initial="hidden"
            animate={controls}
            custom={index}
            variants={slideInVariant}
          >
            <img src={experience.logo} alt="Company Logo" className="logo" />
            <div className="content">
              <p className="role">{experience.role}</p>
              <div className="details">
                <ul>
                  {experience.details.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              </div>
              <p className="technologies">
                Technologies: {experience.technologies}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

export default Experience;
